/**
 * 自建任务批量创建：解析用户选择的本地文件夹（webkitdirectory）。
 * 规则：一级子文件夹 = 一个资产；仅允许一层子目录；预览图为与文件夹同名的 png/jpg；
 * 若存在“与文件夹同名”的 fbx 则为 3D 模型，否则贴图；最多处理 100 个子文件夹。
 */

export const MAX_BATCH_SELF_BUILT_ASSETS = 100;

const IMG_EXT = new Set(['png', 'jpg', 'jpeg']);

/**
 * 归一化浏览器 File 的相对路径。
 * @param {File} file 浏览器 File 对象。
 * @returns {string} 使用正斜杠的路径字符串。
 */
function normalizePath(file) {
    const p = file.webkitRelativePath || file.name || '';
    return p.replace(/\\/g, '/');
}

/**
 * 将路径字符串拆分为分段。
 * @param {string} relPath 相对路径。
 * @returns {string[]} 非空路径分段数组。
 */
function pathPartsFromString(relPath) {
    return relPath.split('/').filter(Boolean);
}

/**
 * 读取 File 的路径分段。
 * @param {File} file 浏览器 File 对象。
 * @returns {string[]} 路径分段。
 */
function pathParts(file) {
    return pathPartsFromString(normalizePath(file));
}

/**
 * 若所有文件首段路径相同则剥去一层（所选父目录名）。
 * @param {File[]} files 文件列表。
 * @returns {number} 需要剥离的层数，0 或 1。
 */
function computeUniformRootStripCount(files) {
    if (!files.length) {
        return 0;
    }
    const rows = files.map((f) => pathParts(f)).filter((p) => p.length > 0);
    if (!rows.length) {
        return 0;
    }
    const root = rows[0][0];
    const allShareSameFirst = rows.every((p) => p.length >= 2 && p[0] === root);
    return allShareSameFirst ? 1 : 0;
}

/**
 * 生成逻辑路径分段函数。
 * @param {number} stripCount 剥离层数。
 * @returns {Function} 入参为 File，返回分段数组。
 */
function createLogicalPartsGetter(stripCount) {
    return (file) => {
        let p = pathParts(file);
        let s = 0;
        while (s < stripCount && p.length >= 2) {
            p = p.slice(1);
            s += 1;
        }
        return p;
    };
}

/**
 * 判断资产文件夹内是否出现嵌套路径。
 * @param {File[]} folderFiles 某文件夹下所有文件。
 * @param {Function} logicalParts 逻辑路径函数。
 * @returns {boolean} 是否存在超过两段的嵌套。
 */
function hasNestedPath(folderFiles, logicalParts) {
    return folderFiles.some((f) => logicalParts(f).length > 2);
}

/**
 * 查找与文件夹同名的预览图。
 * @param {File[]} folderFiles 某文件夹下所有文件。
 * @param {string} folderName 资产文件夹名称。
 * @param {Function} logicalParts 逻辑路径函数。
 * @returns {File|null} 匹配的预览图文件或 null。
 */
function findPreviewMatchingFolderName(folderFiles, folderName, logicalParts) {
    const lowerName = folderName.toLowerCase();
    const candidates = folderFiles.filter((f) => logicalParts(f).length === 2);
    for (let i = 0; i < candidates.length; i += 1) {
        const f = candidates[i];
        const parts = logicalParts(f);
        const base = parts[1];
        const dot = base.lastIndexOf('.');
        if (dot > 0) {
            const stem = base.slice(0, dot);
            const ext = base.slice(dot + 1).toLowerCase();
            if (IMG_EXT.has(ext) && stem.toLowerCase() === lowerName) {
                return f;
            }
        }
    }
    return null;
}

/**
 * 判断直接子文件中是否存在“与文件夹同名”的 FBX。
 * @param {File[]} folderFiles 某文件夹下所有文件。
 * @param {string} folderName 资产文件夹名称。
 * @param {Function} logicalParts 逻辑路径函数。
 * @returns {boolean} 是否存在同名 fbx 文件。
 */
function hasFbxInDirectFiles(folderFiles, folderName, logicalParts) {
    const lowerName = String(folderName || '').toLowerCase();
    const direct = folderFiles.filter((f) => logicalParts(f).length === 2);
    return direct.some((f) => {
        const base = logicalParts(f)[1];
        const dot = base.lastIndexOf('.');
        if (dot <= 0) {
            return false;
        }
        const stem = base.slice(0, dot).toLowerCase();
        const ext = base.slice(dot + 1).toLowerCase();
        return ext === 'fbx' && stem === lowerName;
    });
}

/**
 * 按一级子文件夹分组文件。
 * @param {File[]} files 用户选择的全部文件。
 * @param {Function} logicalParts 逻辑路径函数。
 * @returns {Map<string, File[]>} 文件夹名到文件列表的映射。
 */
function groupFilesByTopFolder(files, logicalParts) {
    const byTop = new Map();
    files.forEach((file) => {
        const parts = logicalParts(file);
        if (parts.length >= 2) {
            const top = parts[0];
            if (!byTop.has(top)) {
                byTop.set(top, []);
            }
            byTop.get(top).push(file);
        }
    });
    return byTop;
}

/**
 * 解析自建任务批量文件夹结构。
 * @param {File[]} files 用户通过 webkitdirectory 选择的文件列表。
 * @returns {Object} 解析结果，含 items 与 skipped 数组。
 */
export function parseSelfBuiltBatchFolder(files) {
    const skipped = [];
    const items = [];

    if (!Array.isArray(files) || files.length === 0) {
        return { items, skipped };
    }

    const stripCount = computeUniformRootStripCount(files);
    const logicalParts = createLogicalPartsGetter(stripCount);
    const byTop = groupFilesByTopFolder(files, logicalParts);

    let folderNames = Array.from(byTop.keys()).sort();
    if (folderNames.length > MAX_BATCH_SELF_BUILT_ASSETS) {
        skipped.push({
            folderName: '*',
            reason: `仅处理前 ${MAX_BATCH_SELF_BUILT_ASSETS} 个子文件夹（共 ${folderNames.length} 个），其余已忽略`
        });
        folderNames = folderNames.slice(0, MAX_BATCH_SELF_BUILT_ASSETS);
    }

    folderNames.forEach((folderName) => {
        const folderFiles = byTop.get(folderName) || [];

        if (hasNestedPath(folderFiles, logicalParts)) {
            skipped.push({ folderName, reason: '仅支持一层子文件夹，检测到嵌套目录，已跳过' });
            return;
        }

        const previewFile = findPreviewMatchingFolderName(folderFiles, folderName, logicalParts);
        if (!previewFile) {
            skipped.push({
                folderName,
                reason: '缺少与文件夹同名的 PNG/JPG 预览图'
            });
            return;
        }

        const hasFbx = hasFbxInDirectFiles(folderFiles, folderName, logicalParts);
        items.push({ folderName, previewFile, hasFbx });
    });

    return { items, skipped };
}

/**
 * 按标签文案在配置列表中查找一项。
 * @param {Array<Object>} list 资产类型列表。
 * @param {string} t 标签关键字。
 * @returns {Object|undefined} 匹配项。
 */
function findAssetTypeByLabelText(list, t) {
    return list.find(
        (x) => x && (x.label === t || (x.label && String(x.label).includes(t)))
    );
}

/**
 * 根据是否含 FBX 解析资产类型 code。
 * @param {boolean} hasFbx 子文件夹内是否含同名 FBX。
 * @param {Object} assetTypeMap 任务配置中的 asset_type 映射。
 * @returns {string|undefined} 匹配的资产类型 code。
 */
export function resolveAssetTypeCodeForBatch(hasFbx, assetTypeMap) {
    const list = Object.values(assetTypeMap || {});
    if (hasFbx) {
        return findAssetTypeByLabelText(list, '3D模型')?.code;
    }
    return findAssetTypeByLabelText(list, '贴图')?.code;
}
